import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Ban, Banknote, CheckCircle2, Download, Eye, Loader2, Mail, Pencil, Plus, ReceiptText, RefreshCw, RotateCcw, Send, Server, Trash2, UserRound, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { AmountInput } from '../components/ui/amount-input';
import { Checkbox } from '../components/ui/checkbox';
import RowActions from '../components/RowActions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { validate } from '../lib/validators';
import { copyText } from '../lib/clipboard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
// Invoice PDFs are in English, so generated line descriptions use English month names (UI uses common:months)
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const STATUS = {
  draft: { label: 'status.draft', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  sent: { label: 'status.sent', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  paid: { label: 'status.paid', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  cancelled: { label: 'status.cancelled', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};
const INCOME = {
  paid: { label: 'income.paid', cls: 'text-emerald-600 dark:text-emerald-400' },
  partial: { label: 'income.partial', cls: 'text-amber-600 dark:text-amber-400' },
  pending: { label: 'income.pending', cls: 'text-slate-500' },
};
// Phones / Safari can hand a PDF straight to the Mail app (Web Share API with files)
const CAN_SHARE_FILES = (() => {
  try { return !!navigator.canShare?.({ files: [new File(['x'], 'x.pdf', { type: 'application/pdf' })] }); } catch { return false; }
})();
// Sharing files needs HTTPS: over plain http (e.g. http://192.168.x.x) browsers hide navigator.share
const INSECURE = typeof window !== 'undefined' && !window.isSecureContext;
const parseEmails = (v) => String(v || '').split(/[,;\s]+/).filter(Boolean);
const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtMoney = (v, cur) => `${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const emptyItem = () => ({ description: '', quantity: '1', unit_price: '' });
const emptyForm = () => {
  const now = new Date();
  return {
    project_id: '', profile_id: '', bill_to_name: '', bill_to_email: '', bill_to_address: '', bill_to_tax_id: '',
    bill_to_contact: '', currency: 'USD', issue_date: today(), due_date: plusDays(today(), 15),
    period_month: String(now.getMonth() + 1), period_year: String(now.getFullYear()), notes: '', items: [emptyItem()],
  };
};

export default function ClientInvoices() {
  const { t } = useTranslation('clientInvoices');
  const MONTHS = t('common:months', { returnObjects: true });
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const canWrite = !['accountant', 'employee', 'pending'].includes(user?.role);
  const [invoices, setInvoices] = useState(null);
  const [projects, setProjects] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profilesReady, setProfilesReady] = useState(false);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editor, setEditor] = useState(null); // null | {id?}
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null); // invoice being sent
  const [sendForm, setSendForm] = useState({ channel: 'email', to: '', recipient: 'me', message: '' });
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sendTouched, setSendTouched] = useState(false);
  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [rampEnabled, setRampEnabled] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [receiving, setReceiving] = useState(null); // invoice whose money arrived
  const [receiveForm, setReceiveForm] = useState({ amount: '', income_date: '', create_income: true, allow_duplicate: false });
  const [receiveCheck, setReceiveCheck] = useState(null); // {linked_income, nearby} for the dialog
  const [mailDraft, setMailDraft] = useState(null); // {to, subject, body} for the user's own mail app
  const [pdfFile, setPdfFile] = useState(null);
  const [recipient, setRecipient] = useState(undefined); // project owner's name printed as "Recipient name"

  // The invoice's "Recipient name" is the selected project's owner (shown read-only in the editor)
  useEffect(() => {
    if (!editor || !form.project_id) { setRecipient(undefined); return undefined; }
    let alive = true;
    axios.get(`${API_URL}/api/client-invoices/prefill/${form.project_id}`, { withCredentials: true })
      .then((res) => { if (alive) setRecipient(res.data?.owner_name || null); })
      .catch(() => { if (alive) setRecipient(undefined); });
    return () => { alive = false; };
  }, [editor, form.project_id]);
  const [askSent, setAskSent] = useState(false); // mail app opened: did they send it?

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterProject !== 'all') params.set('project_id', filterProject);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const res = await axios.get(`${API_URL}/api/client-invoices?${params}`, { withCredentials: true });
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(errorText(err, t('toast.loadFailed')));
      setInvoices([]);
    }
  }, [filterProject, filterStatus, t]);

  const loadProfiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/billing-profiles`, { withCredentials: true });
      setProfiles(Array.isArray(res.data) ? res.data : []);
    } catch { setProfiles([]); }
    setProfilesReady(true);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API_URL}/api/projects`, { withCredentials: true })
      .then((res) => setProjects(Array.isArray(res.data) ? res.data : (res.data?.items || [])))
      .catch(() => setProjects([]));
    loadProfiles();
  }, [loadProfiles]);

  const isAdminUser = ['super_admin', 'admin'].includes(user?.role);
  const errors = {};
  if (!isAdminUser && !form.project_id) errors.project_id = t('errors.projectRequired');
  if (!form.profile_id) errors.profile_id = t('errors.profileRequired');
  if (!form.bill_to_name.trim()) errors.bill_to_name = t('errors.clientNameRequired');
  [['bill_to_email', 'email'], ['bill_to_tax_id', 'tax_id']].forEach(([k, rule]) => { const e = validate(rule, form[k]); if (e) errors[k] = e; });
  if (!form.issue_date) errors.issue_date = t('errors.dateRequired');
  if (form.due_date && form.issue_date && form.due_date < form.issue_date) errors.due_date = t('errors.dueBeforeIssue');
  if (!!form.period_month !== !!form.period_year) errors.period = t('errors.periodPair');
  form.items.forEach((it, i) => {
    const blank = !it.description && !it.unit_price;
    if (blank && form.items.length > 1) return;
    if (!it.description.trim()) errors[`item.${i}.description`] = t('errors.descriptionRequired');
    if (!(parseFloat(it.quantity) > 0)) errors[`item.${i}.quantity`] = t('errors.quantityPositive');
    if (!(parseFloat(it.unit_price) > 0)) errors[`item.${i}.unit_price`] = t('errors.priceRequired');
  });
  const errFor = (k) => ((touched[k] || submitted) ? errors[k] : '');
  const touch = (k) => () => setTouched((prev) => ({ ...prev, [k]: true }));
  const errCls = (k) => (errFor(k) ? 'border-red-500 focus-visible:ring-red-500' : '');
  const errText = (k) => (errFor(k) ? <p className="text-xs text-red-600 mt-1" data-testid={`err-${k}`}>{errFor(k)}</p> : null);
  const sendError = sendForm.channel === 'email' || sendForm.channel === 'device'
    ? ((sendForm.channel === 'email' && !sendForm.to.trim()) ? t('errors.emailRequired') : validate('emails', sendForm.to))
      || (sendForm.message.length > 4000 ? t('errors.messageTooLong', { max: 4000 }) : '')
    : '';

  const total = useMemo(() => form.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0), [form.items]);

  // Project chosen in a new invoice: reuse the client details and lines of its last invoice
  const applyPrefill = useCallback(async (projectId, overrides = {}) => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API_URL}/api/client-invoices/prefill/${projectId}`, { withCredentials: true });
      const { last, last_items: lastItems, estimate } = res.data;
      setForm((f) => {
        const next = { ...f, project_id: projectId };
        if (last) {
          ['bill_to_name', 'bill_to_email', 'bill_to_address', 'bill_to_tax_id', 'bill_to_contact', 'notes']
            .forEach((k) => { if (!f[k] && last[k]) next[k] = last[k]; });
          if (last.profile_id && !f.profile_id) next.profile_id = String(last.profile_id);
          if (last.currency) next.currency = last.currency;
        }
        const blank = f.items.every((it) => !it.description && !it.unit_price);
        if (blank && lastItems?.length) {
          next.items = lastItems.map((it) => ({ description: it.description, quantity: String(it.quantity), unit_price: String(it.unit_price) }));
        } else if (blank && estimate) {
          next.currency = estimate.currency;
          next.items = [{ description: `${res.data.project_name} — ${MONTHS_EN[estimate.month - 1]} ${estimate.year}`, quantity: '1', unit_price: String(estimate.amount) }];
        }
        return { ...next, ...overrides };
      });
    } catch { /* prefill is optional */ }
  }, []);

  const openNew = useCallback((preset = {}) => {
    const base = emptyForm();
    const def = profiles.find((p) => p.is_default && p.kind === 'company') || profiles.find((p) => p.is_default) || profiles[0];
    setForm({ ...base, profile_id: def ? String(def.id) : '', ...preset });
    setTouched({}); setSubmitted(false);
    setEditor({});
    if (preset.project_id) applyPrefill(preset.project_id, preset);
  }, [profiles, applyPrefill]);

  // Arriving from "Expected income" (IncomeEstimates) with ?new=1&project=..&month=..&year=..&amount=..&currency=..
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get('new') && profilesReady && projects.length) {
      const preset = { project_id: q.get('project') || '' };
      if (q.get('month')) preset.period_month = q.get('month');
      if (q.get('year')) preset.period_year = q.get('year');
      if (q.get('currency')) preset.currency = q.get('currency');
      if (q.get('amount')) {
        const p = projects.find((x) => x.project_id === preset.project_id);
        preset.items = [{ description: `${p?.name || 'Services'} — ${MONTHS_EN[(parseInt(preset.period_month, 10) || 1) - 1]} ${preset.period_year || ''}`.trim(), quantity: '1', unit_price: q.get('amount') }];
      }
      navigate('/client-invoices', { replace: true });
      openNew(preset);
    }
  }, [location.search, projects, profilesReady, navigate, openNew]);

  const openEdit = async (inv) => {
    try {
      const res = await axios.get(`${API_URL}/api/client-invoices/${inv.id}`, { withCredentials: true });
      const d = res.data;
      setForm({
        project_id: d.project_id || '', profile_id: d.profile_id ? String(d.profile_id) : '',
        bill_to_name: d.bill_to_name || '', bill_to_email: d.bill_to_email || '', bill_to_address: d.bill_to_address || '',
        bill_to_tax_id: d.bill_to_tax_id || '', bill_to_contact: d.bill_to_contact || '', currency: d.currency,
        issue_date: d.issue_date, due_date: d.due_date || '', period_month: d.period_month ? String(d.period_month) : '',
        period_year: d.period_year ? String(d.period_year) : '', notes: d.notes || '',
        items: d.items.map((it) => ({ description: it.description, quantity: String(it.quantity), unit_price: String(it.unit_price) })),
      });
      setTouched({}); setSubmitted(false);
      setEditor({ id: d.id, number: d.number });
    } catch (err) { toast.error(errorText(err, t('toast.openFailed'))); }
  };

  const applyOwnerProfile = async () => {
    if (!form.project_id) { toast.error(t('toast.selectProjectFirst')); return; }
    try {
      const res = await axios.post(`${API_URL}/api/billing-profiles/from-project-owner/${form.project_id}`, {}, { withCredentials: true });
      await loadProfiles();
      setForm((f) => ({ ...f, profile_id: String(res.data.id) }));
      const hasBank = res.data.bank_hidden || Object.keys(res.data.bank_details || {}).length > 0;
      toast.success(hasBank ? t('toast.ownerProfile', { name: res.data.name }) : t('toast.ownerProfileNoBank', { name: res.data.name }));
    } catch (err) { toast.error(errorText(err, t('toast.ownerProfileFailed'))); }
  };

  const setItem = (i, key, value) => setForm((f) => ({ ...f, items: f.items.map((it, n) => (n === i ? { ...it, [key]: value } : it)) }));

  const save = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length) { toast.error(Object.values(errors)[0]); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        project_id: form.project_id || null,
        profile_id: form.profile_id ? parseInt(form.profile_id, 10) : null,
        period_month: form.period_month ? parseInt(form.period_month, 10) : null,
        period_year: form.period_year ? parseInt(form.period_year, 10) : null,
        due_date: form.due_date || null,
        items: form.items.filter((it) => it.description || it.unit_price).map((it) => ({
          description: it.description, quantity: parseFloat(it.quantity) || 0, unit_price: parseFloat(it.unit_price) || 0,
        })),
      };
      const res = editor?.id
        ? await axios.put(`${API_URL}/api/client-invoices/${editor.id}`, body, { withCredentials: true })
        : await axios.post(`${API_URL}/api/client-invoices`, body, { withCredentials: true });
      toast.success(t('toast.saved', { number: res.data.number }));
      setEditor(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common:status.saveFailed')));
    } finally { setSaving(false); }
  };

  const setStatus = async (inv, status, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const res = await axios.post(`${API_URL}/api/client-invoices/${inv.id}/status`, { status }, { withCredentials: true });
      toast.success(res.data.message || t('toast.updated'));
      load();
    } catch (err) { toast.error(errorText(err, t('toast.updateFailed'))); }
  };

  const syncRamp = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/client-invoices/ramp-sync`, {}, { withCredentials: true });
      toast.success(res.data.message);
      if (res.data.new) load();
    } catch (err) { toast.error(errorText(err, t('receive.syncFailed'))); } finally { setSyncing(false); }
  };

  const openReceive = (inv) => {
    const pay = inv.incoming_payment;
    // a Ramp amount is only a hint when it is in the invoice's currency and from its client
    const hinted = pay && pay.currency === inv.currency && pay.payer_ok !== false;
    setReceiveForm({
      amount: String(hinted ? pay.amount : inv.total),
      income_date: localToday(),
      create_income: !!inv.project_id && inv.income_state !== 'paid',
      allow_duplicate: false,
    });
    setReceiveCheck(null);
    setReceiving(inv);
  };

  // Incomes that may already be this payment (the invoice's own, or a similar one from a bank message)
  useEffect(() => {
    if (!receiving) return undefined;
    let ignore = false;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ amount: receiveForm.amount || '0', income_date: receiveForm.income_date || '' });
        const res = await axios.get(`${API_URL}/api/client-invoices/${receiving.id}/receive-check?${params}`, { withCredentials: true });
        if (ignore) return;
        setReceiveCheck(res.data);
        if (res.data.linked_income) setReceiveForm((f) => ({ ...f, create_income: false }));
      } catch { if (!ignore) setReceiveCheck({ linked_income: null, nearby: [] }); }
    }, 400);
    return () => { ignore = true; clearTimeout(timer); };
  }, [receiving, receiveForm.amount, receiveForm.income_date]);

  const receive = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/client-invoices/${receiving.id}/receive`, {
        amount: Number(receiveForm.amount), income_date: receiveForm.income_date || null, create_income: receiveForm.create_income,
        allow_duplicate: receiveForm.allow_duplicate,
      }, { withCredentials: true });
      toast.success(res.data.message);
      setReceiving(null);
      load();
    } catch (err) { toast.error(errorText(err, t('toast.updateFailed'))); } finally { setBusy(false); }
  };

  const remove = async (inv) => {
    if (!window.confirm(t('confirm.delete', { number: inv.number }))) return;
    try {
      await axios.delete(`${API_URL}/api/client-invoices/${inv.id}`, { withCredentials: true });
      toast.success(t('common:status.deleted'));
      load();
    } catch (err) { toast.error(errorText(err, t('common:status.deleteFailed'))); }
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/client-invoices/send-options`, { withCredentials: true })
      .then((res) => { setSmtpEnabled(!!res.data?.smtp); setRampEnabled(!!res.data?.ramp); })
      .catch(() => setSmtpEnabled(false));
  }, []);

  const openSend = (inv) => {
    setSendForm({ channel: 'device', to: inv.bill_to_email || '', recipient: 'me', message: '' });
    setSendTouched(false); setAskSent(false); setMailDraft(null); setPdfFile(null); setPdfSaved(false);
    setSending(inv);
  };

  // Own mail app: fetch the PDF and the email text ahead of the click, so the click can hand them
  // to the Share sheet / mailto synchronously (browsers only allow that inside the user's gesture)
  useEffect(() => {
    if (!sending || sendForm.channel !== 'device') return undefined;
    let alive = true;
    axios.get(`${API_URL}/api/client-invoices/${sending.id}/pdf?download=true`, { withCredentials: true, responseType: 'blob' })
      .then((res) => { if (alive) setPdfFile(new File([res.data], `${sending.number}.pdf`, { type: 'application/pdf' })); })
      .catch(() => { if (alive) toast.error(t('toast.pdfFailed')); });
    return () => { alive = false; };
  }, [sending, sendForm.channel, t]);
  useEffect(() => {
    if (!sending || sendForm.channel !== 'device' || sendError) return undefined;
    let alive = true;
    const timer = setTimeout(() => {
      axios.post(`${API_URL}/api/client-invoices/${sending.id}/email-draft`,
        { to_emails: parseEmails(sendForm.to), message: sendForm.message || null }, { withCredentials: true })
        .then((res) => { if (alive) setMailDraft(res.data); })
        .catch((err) => { if (alive) toast.error(errorText(err, t('toast.draftFailed'))); });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [sending, sendForm.channel, sendForm.to, sendForm.message, sendError, t]);

  // mailto can't attach files, so the PDF is saved by its own button (doing both in one click loses the
  // download on iPad/iPhone, where opening Mail leaves the page)
  const [pdfSaved, setPdfSaved] = useState(false);
  const savePdf = () => { saveBlob(pdfFile, pdfFile.name); setPdfSaved(true); };
  const openMailto = () => {
    const to = mailDraft.to.map(encodeURIComponent).join(',');
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(mailDraft.subject)}&body=${encodeURIComponent(mailDraft.body)}`;
    setAskSent(true);
  };
  const openMailApp = () => {
    setSendTouched(true);
    if (sendError) { toast.error(sendError); return; }
    if (!mailDraft || !pdfFile) { toast.info(t('toast.preparing')); return; }
    if (CAN_SHARE_FILES) {
      navigator.share({ files: [pdfFile], title: mailDraft.subject, text: mailDraft.body })
        .then(() => setAskSent(true))
        .catch((e) => { if (e?.name !== 'AbortError') openMailto(); });
    } else {
      openMailto();
    }
  };
  const downloadEml = async () => {
    setSendTouched(true);
    if (sendError) { toast.error(sendError); return; }
    try {
      const res = await axios.post(`${API_URL}/api/client-invoices/${sending.id}/eml`,
        { to_emails: parseEmails(sendForm.to), message: sendForm.message || null }, { withCredentials: true, responseType: 'blob' });
      saveBlob(res.data, `${sending.number}.eml`);
      setAskSent(true);
    } catch (err) { toast.error(t('toast.fileFailed')); }
  };
  const confirmSent = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/client-invoices/${sending.id}/mark-sent`, { via: 'email:device' }, { withCredentials: true });
      toast.success(res.data.message || t('toast.marked'));
      setSending(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('toast.markFailed')));
    } finally { setBusy(false); }
  };
  const send = async () => {
    setSendTouched(true);
    if (sendError) { toast.error(sendError); return; }
    setBusy(true);
    try {
      const body = sendForm.channel === 'email'
        ? { channel: 'email', to_emails: sendForm.to.split(/[,;\s]+/).filter(Boolean), message: sendForm.message || null }
        : { channel: 'telegram', recipient: sendForm.recipient };
      const res = await axios.post(`${API_URL}/api/client-invoices/${sending.id}/send`, body, { withCredentials: true });
      toast.success(res.data.message || t('toast.sent'));
      setSending(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('toast.sendFailed')));
    } finally { setBusy(false); }
  };

  const pdfUrl = (inv, download) => `${API_URL}/api/client-invoices/${inv.id}/pdf${download ? '?download=true' : ''}`;
  const selectedProfile = profiles.find((p) => String(p.id) === String(form.profile_id));
  // Non-admins issue under company profiles, their own, or the selected project's owner
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const ownerEmployee = projects.find((p) => p.project_id === form.project_id)?.owner_employee_id;
  const usableProfiles = profiles.filter((p) => isAdmin || p.kind === 'company' || p.user_id === user?.user_id
    || (ownerEmployee && p.employee_id === ownerEmployee) || String(p.id) === String(form.profile_id));
  const years = Array.from({ length: 4 }, (_, i) => String(new Date().getFullYear() - 1 + i));

  return (
    <div className="space-y-6" data-testid="client-invoices-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ReceiptText className="h-7 w-7 text-blue-600" /> {t('title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('subtitle')}</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            {rampEnabled && (
              <Button variant="outline" onClick={syncRamp} disabled={syncing} className="gap-2" title={t('receive.syncHint')} data-testid="ramp-sync-btn">
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> Ramp
              </Button>
            )}
            <Button onClick={() => openNew()} className="gap-2 flex-1 sm:flex-none" data-testid="new-invoice-btn"><Plus className="h-4 w-4" /> {t('newInvoice')}</Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-full sm:w-56"><SelectValue placeholder={t('common:fields.project')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allProjects')}</SelectItem>
            {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allStatuses')}</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{t(v.label)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
        {invoices === null ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : invoices.length === 0 ? (
          <div className="py-12 text-center text-slate-500">{t('empty')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500">
              <tr>
                {['table.number', 'table.project', 'table.client', 'table.issuer', 'table.period', 'table.amount', 'table.status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h ? t(h) : ''}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {invoices.map((inv) => {
                const st = STATUS[inv.status] || STATUS.draft;
                const income = inv.income_state && INCOME[inv.income_state];
                return (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.number}`}>
                    <td className="px-4 py-3 font-mono whitespace-nowrap">{inv.number}<div className="text-xs text-slate-400 font-sans">{inv.issue_date}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap">{inv.project_name || '—'}</td>
                    <td className="px-4 py-3"><div className="font-medium">{inv.bill_to_name}</div><div className="text-xs text-slate-500">{inv.bill_to_email}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap">{inv.issuer_name || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{inv.period_month ? `${MONTHS[inv.period_month - 1]} ${inv.period_year}` : '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold">{fmtMoney(inv.total, inv.currency)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${st.cls}`}>{t(st.label)}</span>
                      {income && inv.status !== 'cancelled' && (
                        <div className={`text-xs mt-1 ${income.cls}`} title={t('income.received', { amount: fmtMoney(inv.income_received, inv.currency) })}>
                          {t(income.label)}
                          {inv.income_period_count > 1 && <div className="text-slate-400">{t('income.periodCount', { n: inv.income_period_count, total: fmtMoney(inv.income_period_total, inv.currency) })}</div>}
                        </div>
                      )}
                      {inv.incoming_payment && !inv.incoming_payment.received_on && ['draft', 'sent'].includes(inv.status) && (
                        <div className={`text-xs mt-1 ${inv.incoming_payment.payer_ok === false ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400'}`}
                          data-testid={`incoming-${inv.number}`}
                          title={t('receive.incomingTitle', { provider: 'Ramp', id: inv.incoming_payment.payment_id, payer: inv.incoming_payment.payer || '—', method: inv.incoming_payment.method || '—', date: inv.incoming_payment.payment_date || '—' })}>
                          {inv.incoming_payment.payer_ok === false && '⚠️ '}
                          {t('receive.onTheWay', { amount: fmtMoney(inv.incoming_payment.amount, inv.incoming_payment.currency) })}
                          {inv.incoming_payment.payer_ok === false && <div>{t('receive.payerMismatch', { payer: inv.incoming_payment.payer || '—' })}</div>}
                          {inv.incoming_payment.eta && <div className="text-slate-400">{t('receive.eta', { date: inv.incoming_payment.eta })}</div>}
                        </div>
                      )}
                      {inv.sent_via && <div className="text-xs text-slate-400 mt-0.5">{inv.sent_via.startsWith('email') ? 'Email' : 'Telegram'}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <RowActions actions={[
                        { key: 'view', label: t('rowActions.view'), hint: t('rowActions.viewHint'), icon: Eye, href: pdfUrl(inv), newTab: true },
                        { key: 'download', label: t('rowActions.download'), icon: Download, href: pdfUrl(inv, true), download: true },
                        canWrite && inv.status === 'draft' && { key: 'edit', label: t('common:actions.edit'), hint: t('rowActions.editHint'), icon: Pencil, onClick: () => openEdit(inv), testId: `edit-${inv.number}` },
                        canWrite && inv.status !== 'cancelled' && { key: 'send', label: t('common:actions.send'), hint: t('rowActions.sendHint'), icon: Send, className: 'text-blue-600', onClick: () => openSend(inv), testId: `send-${inv.number}` },
                        canWrite && ['draft', 'sent'].includes(inv.status) && { key: 'receive', label: t('receive.action'), hint: t('receive.actionHint'), icon: Banknote, className: 'text-emerald-600', onClick: () => openReceive(inv), testId: `receive-${inv.number}` },
                        canWrite && ['draft', 'sent'].includes(inv.status) && { key: 'paid', label: t('rowActions.markPaid'), hint: t('rowActions.markPaidHint'), icon: CheckCircle2, className: 'text-emerald-600', onClick: () => setStatus(inv, 'paid'), testId: `paid-${inv.number}` },
                        canWrite && ['draft', 'sent'].includes(inv.status) && { key: 'cancel', label: t('rowActions.cancel'), hint: t('rowActions.cancelHint'), icon: Ban, className: 'text-amber-600', onClick: () => setStatus(inv, 'cancelled', t('confirm.cancel', { number: inv.number })), testId: `cancel-${inv.number}` },
                        canWrite && ['sent', 'paid', 'cancelled'].includes(inv.status) && { key: 'reopen', label: t('rowActions.reopen'), hint: t('rowActions.reopenHint'), icon: RotateCcw, onClick: () => setStatus(inv, 'draft', t('confirm.reopen', { number: inv.number })), testId: `reopen-${inv.number}` },
                        canWrite && inv.status === 'draft' && { key: 'delete', label: t('common:actions.delete'), hint: t('rowActions.deleteHint'), icon: Trash2, className: 'text-red-500', onClick: () => remove(inv), testId: `delete-${inv.number}` },
                      ]} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Editor */}
      <Dialog open={!!editor} onOpenChange={(v) => { if (!v && !saving) setEditor(null); }}>
        <DialogContent className="max-w-3xl" data-testid="invoice-editor">
          <DialogHeader>
            <DialogTitle>{editor?.id ? t('editor.editTitle', { number: editor.number }) : t('newInvoice')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t('common:fields.project')}</Label>
                <Select value={form.project_id || undefined} onValueChange={(v) => { setForm((f) => ({ ...f, project_id: v })); if (!editor?.id) applyPrefill(v); }}>
                  <SelectTrigger data-testid="invoice-project" className={errCls('project_id')}><SelectValue placeholder={t('errors.projectRequired')} /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                {errText('project_id')}
              </div>
              <div className="space-y-1.5">
                <Label>{t('editor.issuer')}</Label>
                <div className="flex gap-2">
                  <Select value={form.profile_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, profile_id: v }))}>
                    <SelectTrigger data-testid="invoice-profile" className={errCls('profile_id')}><SelectValue placeholder={t('editor.profilePlaceholder')} /></SelectTrigger>
                    <SelectContent>
                      {usableProfiles.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.kind === 'company' ? '🏢 ' : '👤 '}{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button type="button" variant="outline" size="icon" className="shrink-0" aria-label={t('editor.ownerProfile')} onClick={applyOwnerProfile}><UserRound className="h-4 w-4" /></Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[240px]">
                        <p className="font-medium">{t('editor.ownerProfile')}</p>
                        <p className="opacity-80 mt-0.5">{t('editor.ownerProfileHint')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {errText('profile_id')}
                {selectedProfile && !selectedProfile.bank_hidden && !Object.keys(selectedProfile.bank_details || {}).length && (
                  <p className="text-xs text-amber-600">{t('editor.profileNoBank')}</p>
                )}
                {!profiles.length && <p className="text-xs text-amber-600">{t('editor.noProfiles')}</p>}
              </div>
            </div>
            {recipient !== undefined && (
              <div className="rounded-lg bg-slate-50 dark:bg-slate-900/40 px-3 py-2 text-sm" data-testid="invoice-recipient">
                <span className="text-slate-500">{t('recipient.label')}: </span>
                {recipient ? <span className="font-medium">{recipient}</span> : <span className="text-amber-600">{t('recipient.none')}</span>}
                <p className="text-xs text-slate-500 mt-0.5">{t('recipient.hint')}</p>
              </div>
            )}

            <div>
              <p className="text-sm font-medium mb-2">{t('editor.billTo')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Input placeholder={`${t('editor.companyName')} *`} value={form.bill_to_name} className={errCls('bill_to_name')} onBlur={touch('bill_to_name')} onChange={(e) => setForm({ ...form, bill_to_name: e.target.value })} data-testid="invoice-bill-to" />{errText('bill_to_name')}</div>
                <div><Input placeholder={t('editor.emailPlaceholder')} type="email" value={form.bill_to_email} className={errCls('bill_to_email')} onBlur={touch('bill_to_email')} onChange={(e) => setForm({ ...form, bill_to_email: e.target.value })} data-testid="invoice-bill-email" />{errText('bill_to_email')}</div>
                <Input placeholder={t('editor.contactPlaceholder')} value={form.bill_to_contact} onChange={(e) => setForm({ ...form, bill_to_contact: e.target.value })} />
                <div><Input placeholder={t('editor.taxIdPlaceholder')} value={form.bill_to_tax_id} className={errCls('bill_to_tax_id')} onBlur={touch('bill_to_tax_id')} onChange={(e) => setForm({ ...form, bill_to_tax_id: e.target.value })} />{errText('bill_to_tax_id')}</div>
              </div>
              <Textarea className="mt-3" rows={2} placeholder={t('editor.address')} value={form.bill_to_address} onChange={(e) => setForm({ ...form, bill_to_address: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
              <div className="space-y-1.5"><Label>{t('common:fields.currency')}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['USD', 'UZS', 'EUR'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>{t('common:fields.date')}</Label><Input type="date" value={form.issue_date} className={errCls('issue_date')} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t('editor.dueDate')}</Label><Input type="date" value={form.due_date} className={errCls('due_date')} onBlur={touch('due_date')} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t('editor.periodMonth')}</Label>
                <Select value={form.period_month || 'none'} onValueChange={(v) => setForm({ ...form, period_month: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>{t('common:fields.year')}</Label>
                <Select value={form.period_year || 'none'} onValueChange={(v) => setForm({ ...form, period_year: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {(errText('issue_date') || errText('due_date') || errText('period')) && (
              <div className="-mt-3">{errText('issue_date')}{errText('due_date')}{errText('period')}</div>
            )}
            <div>
              <p className="text-sm font-medium mb-2">{t('editor.items')}</p>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_80px_140px_120px_auto] gap-2 items-center rounded-lg border border-slate-200 dark:border-slate-700 p-2 sm:border-0 sm:p-0">
                    <Input className={`col-span-2 sm:col-span-1 ${errCls(`item.${i}.description`)}`} placeholder={t('editor.itemDescriptionPlaceholder')} value={it.description} onBlur={touch(`item.${i}.description`)} onChange={(e) => setItem(i, 'description', e.target.value)} data-testid={`item-desc-${i}`} />
                    <Input placeholder={t('editor.quantity')} inputMode="decimal" title={t('editor.quantityHint')} className={errCls(`item.${i}.quantity`)} onBlur={touch(`item.${i}.quantity`)} value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value.replace(',', '.').replace(/[^\d.]/g, ''))} />
                    <AmountInput placeholder={t('editor.price')} title={t('editor.unitPrice')} className={errCls(`item.${i}.unit_price`)} onBlur={touch(`item.${i}.unit_price`)} value={it.unit_price} onChange={(v) => setItem(i, 'unit_price', v)} data-testid={`item-price-${i}`} />
                    <div className="text-sm text-right font-medium whitespace-nowrap">{fmtMoney((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), form.currency)}</div>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-400" disabled={form.items.length === 1}
                      title={t('editor.removeLine')} onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, n) => n !== i) }))}><X className="h-4 w-4" /></Button>
                    {[errFor(`item.${i}.description`), errFor(`item.${i}.quantity`), errFor(`item.${i}.unit_price`)].some(Boolean) && (
                      <p className="col-span-full text-xs text-red-600" data-testid={`err-item-${i}`}>
                        {[errFor(`item.${i}.description`), errFor(`item.${i}.quantity`), errFor(`item.${i}.unit_price`)].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2">
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }))}><Plus className="h-3.5 w-3.5" /> {t('editor.addLine')}</Button>
                <div className="text-right"><span className="text-sm text-slate-500 mr-2">{t('common:fields.total')}:</span><span className="text-lg font-bold" data-testid="invoice-total">{fmtMoney(total, form.currency)}</span></div>
              </div>
            </div>

            <div className="space-y-1.5"><Label>{t('editor.notes')}</Label>
              <Textarea rows={2} placeholder={t('editor.notesPlaceholder')} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)} disabled={saving}>{t('common:actions.cancel')}</Button>
            <Button onClick={save} disabled={saving} data-testid="invoice-save">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('editor.saveDraft')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Money received */}
      <Dialog open={!!receiving} onOpenChange={(v) => { if (!v && !busy) setReceiving(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('receive.title', { number: receiving?.number })}</DialogTitle></DialogHeader>
          {receiving && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">{t('receive.intro', { total: fmtMoney(receiving.total, receiving.currency) })}</p>
              <div className="space-y-1">
                <Label>{t('receive.amount', { currency: receiving.currency })}</Label>
                <AmountInput value={receiveForm.amount} onChange={(v) => setReceiveForm((f) => ({ ...f, amount: v }))} data-testid="receive-amount" />
              </div>
              <div className="space-y-1">
                <Label>{t('receive.date')}</Label>
                <Input type="date" value={receiveForm.income_date} onChange={(e) => setReceiveForm((f) => ({ ...f, income_date: e.target.value }))} />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox className="mt-0.5" checked={receiveForm.create_income} disabled={!receiving.project_id || !!receiveCheck?.linked_income}
                  onCheckedChange={(v) => setReceiveForm((f) => ({ ...f, create_income: !!v }))} data-testid="receive-create-income" />
                <span>
                  {t('receive.createIncome', {
                    project: receiving.project_name || '—',
                    period: receiving.period_month ? `${MONTHS[receiving.period_month - 1]} ${receiving.period_year}` : t('receive.dateMonth'),
                  })}
                  {receiving.income_state && receiving.income_state !== 'pending' && (
                    <span className="block text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      {t('receive.alreadyRecorded', { amount: fmtMoney(receiving.income_received, receiving.currency) })}
                    </span>
                  )}
                </span>
              </label>
              {receiveCheck?.linked_income && (
                <p className="text-xs text-amber-600 dark:text-amber-400">{t('receive.linked', { amount: fmtMoney(receiveCheck.linked_income.amount, receiveCheck.linked_income.currency) })}</p>
              )}
              {!receiveCheck?.linked_income && receiveCheck?.nearby?.length > 0 && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs space-y-1">
                  <p className="font-medium text-amber-700 dark:text-amber-300">{t('receive.nearby')}</p>
                  {receiveCheck.nearby.map((inc) => (
                    <p key={inc.income_id} className="text-slate-600 dark:text-slate-300">
                      {fmtMoney(inc.amount, inc.currency)} · {inc.income_date || `${inc.month}/${inc.year}`}{inc.description ? ` · ${inc.description.slice(0, 60)}` : ''}
                    </p>
                  ))}
                  {receiveForm.create_income && (
                    <label className="flex items-center gap-2 pt-1 text-amber-700 dark:text-amber-300">
                      <Checkbox checked={receiveForm.allow_duplicate} onCheckedChange={(v) => setReceiveForm((f) => ({ ...f, allow_duplicate: !!v }))} />
                      {t('receive.allowDuplicate')}
                    </label>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiving(null)} disabled={busy}>{t('common:actions.cancel')}</Button>
            <Button onClick={receive} disabled={busy || !(Number(receiveForm.amount) > 0) || (receiveForm.create_income && !receiveCheck)
              || (receiveForm.create_income && receiveCheck?.nearby?.length > 0 && !receiveForm.allow_duplicate)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="receive-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('receive.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send */}
      <Dialog open={!!sending} onOpenChange={(v) => { if (!v && !busy) setSending(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('send.title', { number: sending?.number })}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className={`grid gap-2 ${smtpEnabled ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {[['device', 'send.channelDevice', Mail], smtpEnabled && ['email', 'send.channelServer', Server], ['telegram', 'send.channelTelegram', Send]].filter(Boolean).map(([k, labelKey, Icon]) => (
                <button key={k} type="button" onClick={() => { setSendForm((f) => ({ ...f, channel: k })); setAskSent(false); }} data-testid={`channel-${k}`}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border-2 p-2.5 text-sm text-center ${sendForm.channel === k
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-slate-200 dark:border-slate-700 text-slate-500'}`}>
                  <Icon className="h-4 w-4" /> {t(labelKey)}
                </button>
              ))}
            </div>
            {askSent ? (
              <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-3" data-testid="ask-sent">
                <p className="text-sm font-medium">{t('send.askSent')}</p>
                <p className="text-xs text-slate-600 dark:text-slate-400">{t('send.askSentHint')}</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={confirmSent} disabled={busy} data-testid="confirm-sent">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('send.yesSent')}</Button>
                  <Button size="sm" variant="outline" onClick={() => setAskSent(false)} disabled={busy}>{t('send.notYet')}</Button>
                </div>
              </div>
            ) : sendForm.channel !== 'telegram' ? (
              <>
                <div className="space-y-1.5"><Label>{t('send.to')}</Label>
                  <Input value={sendForm.to} onBlur={() => setSendTouched(true)} onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })} placeholder="ap@client.com, finance@client.com" data-testid="send-to"
                    className={sendTouched && sendError ? 'border-red-500 focus-visible:ring-red-500' : ''} />
                  {sendTouched && sendError && <p className="text-xs text-red-600" data-testid="send-error">{sendError}</p>}
                </div>
                <div className="space-y-1.5"><Label>{t('send.message')}</Label>
                  <Textarea rows={3} value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} placeholder={t('send.messagePlaceholder')} />
                </div>
                {sendForm.channel === 'email' ? (
                  <p className="text-xs text-slate-500">{t('send.serverHint')}</p>
                ) : CAN_SHARE_FILES ? (
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>{t('send.shareHint')}</p>
                    {parseEmails(sendForm.to).length > 0 && (
                      <p>{t('send.pickRecipient')}{' '}
                        <button type="button" className="text-blue-600 hover:underline" onClick={() => copyText(parseEmails(sendForm.to).join(', ')).then((ok) => ok && toast.success(t('send.emailCopied')))}>
                          {parseEmails(sendForm.to).join(', ')} ({t('send.copy')})
                        </button>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 space-y-1.5">
                    <p>
                      <Trans t={t} i18nKey="send.manualSteps" components={{ b: <b /> }} />
                    </p>
                    {INSECURE && (
                      <p className="rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 px-2 py-1.5" data-testid="insecure-hint">
                        {t('send.insecureHint')}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <Label>{t('send.telegramTo')}</Label>
                {[['me', 'send.recipientMe'], ['owner', 'send.recipientOwner']].map(([k, labelKey]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" checked={sendForm.recipient === k} onChange={() => setSendForm({ ...sendForm, recipient: k })} /> {t(labelKey)}
                  </label>
                ))}
                <p className="text-xs text-slate-500">{t('send.telegramHint')}</p>
              </div>
            )}
            {sending?.status === 'draft' && !askSent && <p className="text-xs text-slate-500">{t('send.draftHint')}</p>}
          </div>
          <DialogFooter className="gap-2 sm:space-x-0 sm:flex-wrap">
            <Button variant="outline" onClick={() => setSending(null)} disabled={busy}>{t('common:actions.cancel')}</Button>
            {askSent ? null : sendForm.channel === 'device' ? (
              <>
                {!CAN_SHARE_FILES && <Button variant="outline" onClick={downloadEml} data-testid="send-eml">.eml (Outlook)</Button>}
                {!CAN_SHARE_FILES && (
                  <Button variant={pdfSaved ? 'outline' : 'default'} onClick={savePdf} disabled={!pdfFile} data-testid="save-pdf">
                    {pdfSaved ? <CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" /> : <Download className="h-4 w-4 mr-1" />} {t('send.step1Pdf')}
                  </Button>
                )}
                <Button onClick={openMailApp} disabled={!mailDraft || !pdfFile} variant={CAN_SHARE_FILES || pdfSaved ? 'default' : 'outline'} data-testid="send-device">
                  {(!mailDraft || !pdfFile) && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {CAN_SHARE_FILES ? t('send.openMail') : t('send.step2Mail')}
                </Button>
              </>
            ) : (
              <Button onClick={send} disabled={busy} data-testid="send-confirm">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('common:actions.send')}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
